import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { PaymentService, PersonalInfoService, DocumentService, ApplicationFormService, AppointmentService } from '@/integrations/supabase/services';

export interface CompletionStatus {
  package: boolean;
  personalInfo: boolean;
  documents: boolean;
  applicationForm: boolean;
  appointment: boolean;
}

export const useCompletionStatus = () => {
  const { user } = useAuth();
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus>({
    package: false,
    personalInfo: false,
    documents: false,
    applicationForm: false,
    appointment: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      checkCompletionStatus();
    }
  }, [user]);

  const checkCompletionStatus = async () => {
    if (!user) return;

    try {
      // Check package selection
      const payments = await PaymentService.getByUserId(user.uid);
      const packageData = payments.find(p => p.status === 'completed');

      // Check personal info
      const personalInfoList = await PersonalInfoService.getByUserId(user.uid);
      const personalInfo = personalInfoList.length > 0 ? personalInfoList[0] : null;

      // Check documents
      const documents = await DocumentService.getByUserId(user.uid);

      // Check application form
      const applicationForms = await ApplicationFormService.getByUserId(user.uid);
      const applicationForm = applicationForms.length > 0 ? applicationForms[0] : null;

      // Check appointments
      const appointments = await AppointmentService.getByUserId(user.uid);

      const status = {
        package: !!packageData,
        personalInfo: !!personalInfo,
        documents: documents && documents.length >= 7, // Requiring all 7 documents
        applicationForm: !!applicationForm,
        appointment: appointments && appointments.length > 0
      };

      setCompletionStatus(status);
    } catch (error) {
      console.error('Error checking completion status:', error);
    } finally {
      setLoading(false);
    }
  };

  return { completionStatus, loading, checkCompletionStatus };
}; 